import { supabase } from './supabaseClient';

/**
 * Comprehensive Audit Trail Service
 * Handles all audit logging operations with advanced filtering and search
 */

/**
 * Fetch audit logs with advanced filtering
 */
export const fetchAuditLogs = async (filters = {}) => {
  try {
    let query = supabase?.from('audit_logs')?.select('*')?.order('event_timestamp', { ascending: false });

    // Apply filters
    if (filters?.user_id) {
      query = query?.eq('user_id', filters?.user_id);
    }
    
    if (filters?.action_type) {
      query = query?.eq('action_type', filters?.action_type);
    }
    
    if (filters?.entity_type) {
      query = query?.eq('entity_type', filters?.entity_type);
    }
    
    if (filters?.severity_level) {
      query = query?.eq('severity_level', filters?.severity_level);
    }
    
    if (filters?.start_date) {
      query = query?.gte('event_timestamp', filters?.start_date);
    }
    
    if (filters?.end_date) {
      query = query?.lte('event_timestamp', filters?.end_date);
    }
    
    if (filters?.search_term) {
      // Use the full-text search function
      const { data: searchResults, error: searchError } = await supabase?.rpc('search_audit_logs', { 
          p_search_term: filters?.search_term,
          p_limit: filters?.limit || 100
        });
      
      if (searchError) throw searchError;
      return { data: searchResults, error: null };
    }
    
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    return { data: null, error };
  }
};

/**
 * Search audit logs with full-text search
 */
export const searchAuditLogs = async (searchTerm, limit = 100) => {
  try {
    const { data, error } = await supabase?.rpc('search_audit_logs', {
        p_search_term: searchTerm,
        p_limit: limit
      });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error searching audit logs:', error);
    throw error;
  }
};

/**
 * Get audit log details by ID
 */
export const getAuditLogById = async (logId) => {
  try {
    const { data, error } = await supabase?.from('audit_logs')?.select('*')?.eq('id', logId)?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching audit log details:', error);
    throw error;
  }
};

/**
 * Get audit statistics for dashboard
 */
export const getAuditStatistics = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase?.from('audit_statistics')?.select('*')?.gte('activity_date', startDate?.toISOString())?.order('activity_date', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching audit statistics:', error);
    throw error;
  }
};

/**
 * Get user activity summary
 */
export const getUserActivitySummary = async () => {
  try {
    const { data, error } = await supabase?.from('user_activity_summary')?.select('*')?.order('total_actions', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching user activity summary:', error);
    throw error;
  }
};

/**
 * Create manual audit log entry
 */
export const createAuditLog = async (logData) => {
  try {
    const { data, error } = await supabase?.rpc('create_audit_log', {
        p_action_type: logData?.actionType,
        p_entity_type: logData?.entityType,
        p_entity_id: logData?.entityId,
        p_entity_name: logData?.entityName || null,
        p_old_values: logData?.oldValues || null,
        p_new_values: logData?.newValues || null,
        p_reason: logData?.reason || null,
        p_severity: logData?.severity || 'INFO',
        p_metadata: logData?.metadata || {}
      });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error creating audit log:', error);
    throw error;
  }
};

/**
 * Get audit logs for specific entity
 */
export const getEntityAuditHistory = async (entityType, entityId) => {
  try {
    const { data, error } = await supabase?.from('audit_logs')?.select('*')?.eq('entity_type', entityType)?.eq('entity_id', entityId)?.order('timestamp', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching entity audit history:', error);
    throw error;
  }
};

/**
 * Get recent audit activities (last 24 hours)
 */
export const getRecentAuditActivities = async (limit = 20) => {
  try {
    const oneDayAgo = new Date();
    oneDayAgo?.setHours(oneDayAgo?.getHours() - 24);

    const { data, error } = await supabase?.from('audit_logs')?.select('*')?.gte('timestamp', oneDayAgo?.toISOString())?.order('timestamp', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching recent audit activities:', error);
    throw error;
  }
};

/**
 * Get anomaly detection data
 */
export const detectAnomalies = async (days = 7) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase?.from('audit_logs')?.select('*')?.gte('timestamp', startDate?.toISOString())?.in('severity_level', ['ERROR', 'CRITICAL'])?.order('timestamp', { ascending: false });

    if (error) throw error;

    // Calculate anomaly patterns
    const anomalies = data?.reduce((acc, log) => {
      const hour = new Date(log.timestamp)?.getHours();
      const key = `${log?.user_email}-${log?.action_type}-${hour}`;
      
      if (!acc?.[key]) {
        acc[key] = {
          user_email: log?.user_email,
          action_type: log?.action_type,
          hour: hour,
          count: 0,
          severity: log?.severity_level
        };
      }
      acc[key].count++;
      return acc;
    }, {});

    return Object.values(anomalies || {})?.filter(a => a?.count > 5);
  } catch (error) {
    console.error('Error detecting anomalies:', error);
    throw error;
  }
};

/**
 * Export audit logs to CSV
 */
export const exportAuditLogsToCSV = async (filters = {}) => {
  try {
    const { data, error } = await fetchAuditLogs({ ...filters, limit: 10000 });
    
    if (error) throw error;
    if (!data || data?.length === 0) {
      throw new Error('Nenhum registro encontrado para exportação');
    }

    // CSV headers
    const headers = [
      'Data/Hora',
      'Usuário',
      'Função',
      'Ação',
      'Tipo de Entidade',
      'Nome da Entidade',
      'Resumo das Mudanças',
      'Motivo',
      'Severidade',
      'IP',
      'User Agent'
    ];

    // Convert data to CSV rows
    const rows = data?.map(log => [
      new Date(log.event_timestamp)?.toLocaleString('pt-BR'),
      log?.user_email,
      log?.user_role,
      log?.action_type,
      log?.entity_type,
      log?.entity_name || '-',
      log?.changes_summary || '-',
      log?.reason || '-',
      log?.severity_level,
      log?.ip_address || '-',
      log?.user_agent || '-'
    ]);

    // Create CSV content
    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.join(','))
    ]?.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit_logs_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Error exporting audit logs to CSV:', error);
    throw error;
  }
};

/**
 * Get action type distribution
 */
export const getActionTypeDistribution = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase?.from('audit_logs')?.select('action_type')?.gte('timestamp', startDate?.toISOString());

    if (error) throw error;

    const distribution = data?.reduce((acc, log) => {
      acc[log.action_type] = (acc?.[log?.action_type] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(distribution || {})?.map(([action, count]) => ({
      action,
      count
    }));
  } catch (error) {
    console.error('Error getting action type distribution:', error);
    throw error;
  }
};
function getAuditLogs(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getAuditLogs is not implemented yet.', args);
  return null;
}

export { getAuditLogs };
function exportAuditLogs(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: exportAuditLogs is not implemented yet.', args);
  return null;
}

export { exportAuditLogs };